import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Use sessionStorage so tokens are cleared when the browser/tab is closed
const storage = {
  get: (key) => sessionStorage.getItem(key),
  set: (key, val) => sessionStorage.setItem(key, val),
  remove: (key) => sessionStorage.removeItem(key),
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(storage.get('token'));
  const [loading, setLoading] = useState(true);

  // Check session timeout (30-minute inactivity)
  // Tracked purely via a ref, never React state - activity fires on every
  // mousemove/keydown/click/scroll, and setState there would re-render every
  // consumer of this context on every mouse movement (this actually happened:
  // it drove a runaway polling loop in Admin -> Investec -> Client Repayments,
  // see memory/HANDOFF_FIX_INSTRUCTIONS.md 2026-09-10).
  const lastActivityRef = React.useRef(Date.now());
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Define logout first so it can be used in the session timeout effect
  const logout = useCallback(() => {
    storage.remove('token');
    setToken(null);
    setUser(null);
    try { sessionStorage.removeItem('admin_preview_mode'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!token) return;

    const checkSession = () => {
      const now = Date.now();
      if (now - lastActivityRef.current > SESSION_TIMEOUT) {
        logout();
      }
    };

    const interval = setInterval(checkSession, 60000); // Check every minute

    // Activity listeners
    const handleActivity = () => updateActivity();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity, true);
    };
  }, [token, logout, updateActivity]);

  // Verify token on mount and check for impersonation token in URL
  useEffect(() => {
    const verifyToken = async () => {
      // Check for impersonation token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateToken = urlParams.get('impersonate');
      
      const tokenToUse = impersonateToken || token;
      
      if (!tokenToUse) {
        setLoading(false);
        return;
      }

      // If using impersonation token, store it
      if (impersonateToken) {
        storage.set('token', impersonateToken);
        setToken(impersonateToken);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${tokenToUse}` }
        });
        setUser(response.data);
        if (impersonateToken) {
          setToken(impersonateToken);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        storage.remove('token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    
    if (response.data.requires_account_selection && response.data.accounts?.length > 0) {
      return {
        requires_2fa: false,
        requires_account_selection: true,
        accounts: response.data.accounts,
        user: null
      };
    }
    
    if (response.data.requires_2fa) {
      return { requires_2fa: true, requires_account_selection: false, user: response.data.user };
    }

    if (response.data.requires_mfa_setup) {
      // Account is required to enroll in 2FA and hasn't yet — no session is
      // created. The caller must complete setup with mfa_setup_token before
      // calling completeMfaSetupLogin().
      return {
        requires_2fa: false,
        requires_account_selection: false,
        requires_mfa_setup: true,
        mfa_setup_token: response.data.mfa_setup_token,
        user: response.data.user,
      };
    }

    const { access_token, user: userData } = response.data;
    storage.set('token', access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return { requires_2fa: false, requires_account_selection: false, user: userData };
  };

  // Called after a forced first-time MFA enrollment (see requires_mfa_setup
  // above) succeeds — the backend mints a real session token as part of the
  // verify-setup response so the user doesn't have to log in a second time.
  const completeMfaSetupLogin = (accessToken, userData) => {
    storage.set('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    lastActivityRef.current = Date.now();
  };

  const selectAccount = async (email, password, accountId) => {
    const response = await axios.post(`${API_URL}/api/auth/select-account`, {
      email,
      password,
      account_id: accountId
    });
    
    if (response.data.requires_2fa) {
      return { requires_2fa: true, user: response.data.user };
    }

    if (response.data.requires_mfa_setup) {
      return {
        requires_2fa: false,
        requires_mfa_setup: true,
        mfa_setup_token: response.data.mfa_setup_token,
        user: response.data.user,
      };
    }

    const { access_token, user: userData } = response.data;
    storage.set('token', access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return { requires_2fa: false, user: userData };
  };

  const loginWith2FA = async (email, password, totpCode, accountId = null) => {
    const response = await axios.post(`${API_URL}/api/auth/login-2fa`, {
      email,
      password,
      totp_code: totpCode,
      account_id: accountId
    });
    
    const { access_token, user: userData } = response.data;
    storage.set('token', access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return userData;
  };

  const signup = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, data);
    const { access_token, user: userData } = response.data;
    storage.set('token', access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return userData;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
      token: resetToken,
      new_password: newPassword
    });
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(
      `${API_URL}/api/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const setup2FA = async () => {
    const response = await axios.post(
      `${API_URL}/api/auth/2fa/setup`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const verify2FA = async (code) => {
    const response = await axios.post(
      `${API_URL}/api/auth/2fa/verify`,
      { code },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Update user state to reflect 2FA enabled
    setUser(prev => ({ ...prev, totp_enabled: true }));
    return response.data;
  };

  const disable2FA = async (code) => {
    const response = await axios.post(
      `${API_URL}/api/auth/2fa/disable`,
      { code },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({ ...prev, totp_enabled: false }));
    return response.data;
  };

  // Sibling accounts reachable via the current user's own email (managed
  // accounts, or their own email on more than one profile) — powers the
  // in-portal account switcher so people don't have to log out/in.
  const getLinkedAccounts = async () => {
    const response = await axios.get(`${API_URL}/api/auth/linked-accounts`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.accounts || [];
  };

  // Switch straight to a sibling account. No password/2FA re-entry — access
  // was already proven for this email at original login.
  const switchAccount = async (accountId) => {
    const response = await axios.post(
      `${API_URL}/api/auth/switch-account`,
      { account_id: accountId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const { access_token, user: userData } = response.data;
    storage.set('token', access_token);
    setToken(access_token);
    setUser(userData);
    lastActivityRef.current = Date.now();
    return userData;
  };

  const value = React.useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    isInvestor: user?.role === 'investor',
    isCompliance: user?.role === 'compliance',
    isIR: user?.role === 'investor_relations',
    isAnalyst: user?.role === 'analyst',
    isStaff: ['admin', 'compliance', 'investor_relations', 'analyst', 'operations'].includes(user?.role),
    // Roles that land in the admin portal (AdminLayout) — narrower than
    // isStaff, since compliance/investor_relations get their own dedicated
    // portals (/compliance, /ir) instead.
    isAdminPortalUser: ['admin', 'analyst', 'operations'].includes(user?.role),
    login,
    loginWith2FA,
    completeMfaSetupLogin,
    selectAccount,
    getLinkedAccounts,
    switchAccount,
    signup,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    setup2FA,
    verify2FA,
    disable2FA
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, token, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;

};
